import { makeStyles } from "@mui/styles";
import React, { useEffect, useState, useRef } from "react";
import { isUserAuthorizedByServer } from "../api/userAccess";
import { logout } from "../util/authentication";
import { getEmployeeStation, getEmployeeStationList, sortOnCaseSensitiveTexts } from "../util/commonUtils";
import UnauthorizedMessage from "../components/UnauthorizedMessage";
import PageLoading from "../components/PageLoading";
import { REQUEST_WOP_TITLE, STATION_FILTER_MUST_BE_SELECTED, STATUS_FILTER_MUST_BE_SELECTED } from "../util/messageConstants";
import {
  ALL_TOR_WOP_STATUSES,
  API_POLLING_INTERVAL,
  API_TIMEOUT,
  BREADCRUMBS_TITLE,
  FEATURES,
  HOME_PATH,
  STATION,
  TIME_OFF_WOP_TYPES_BY_CONTRACT,
} from "../util/constants";
import StarsBreadcrumbs from "../components/fundamentals/StarsBreadcrumbs";
import { Button, Grid, InputAdornment, TextField, Typography } from "@mui/material";
import PropTypes from "prop-types";
import StarsSelectInput from "../components/fundamentals/StarsSelectInput";
import { selectStationFilter, selectUserSession, updateStationFilter } from "../slices/userSession/userSessionSlice";
import { useDispatch, useSelector } from "react-redux";
import { getUnionContractGivenWorkgroup, getWorkgroupGivenJobCode } from "../util/workgroupMapper";
import StarsDatePicker from "../components/fundamentals/StarsDatePicker";
import CancelIconButton from "../components/fundamentals/CancelIconButton";
import SearchButton from "../components/fundamentals/SearchButton";
import TimeOffRequestWOPTable from "../components/tor/TimeOffRequestWOPTable";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import {
  selectEmployeeTimeOffRequestWOPData,
  updateSearchString,
  updateSearchStringErrors,
  updateSelectedDate,
  updateSelectedWopType,
  updateAvailableWopTypes,
  updateEmployeeTimeOffRequestFoundByReferenceNumberSearchIsLoading,
  updateEmployeeTimeOffRequestFoundByReferenceNumberSearch,
  updateEmployeeTimeOffRequestFoundByReferenceNumberSearchIsDialogOpen,
  updateSelectedWorkgroup,
} from "../slices/timeOffRequest/employeeTimeOffRequestWOPSlice";
import {
  fetchLatestTimeOffRequestWOPFreezeEvent,
  fetchTimeOffRequestSnapshotByReferenceNumber,
  fetchTimeOffRequestSnapshotByTorId,
  fetchTimeOffRequestWOPEvents,
} from "../api/timeOffRequest";
import { getScrubbedTimeOffRequestSnapshot, getScrubbedTimeOffRequestSnapshots } from "../util/timeOffRequestScrubber";
import { initializeWOPSearchIndex, searchTimeOffRequestsWOP } from "../util/searchTimeOffRequests";
import { timeOffRequestDataErrorCodeToErrorMessageMap } from "../util/errorMapper";
import { FIFTEEN_DAYS_IN_FUTURE, TODAY, TOMORROW } from "../util/dateHelper";
import {
  convert_Luxon_To_DateFns,
  deserializeDateFromRedux,
  getIsoDateFormat,
  serializeDateToRedux,
} from "../util/dateTimeFormatter";
import { isLWOPTOWOPDayOfRequest } from "../util/timeOffRequestUtils";
import EmployeeTimeOffWOPMobilePage from "./EmployeeTimeOffWOPMobilePage";
import EmployeeTimeOffWOPMobilePageFilterDialog from "./EmployeeTimeOffWOPMobilePageFilterDialog";
import { selectWebSocketEventData } from "../slices/webSocketEvent/webSocketEventSlice";
import { mergeWebSocketAdminTORs, mergeWebSocketFreezes } from "../util/webSocketUtils";
import { DateTime } from "luxon";

const useStyles = makeStyles((theme) => ({
  root: {
    margin: "auto",
    width: "100%",
  },
  pageTitle: {
    marginLeft: "2rem",
    fontSize: "24px",
    maxWidth: "90vw",
    textAlign: "left",
    marginTop: "10px",
  },
  inputRow: {
    width: "100%",
    display: "flex",
  },
  inputStation: {
    width: "5vw",
    // marginRight: "1vw",
  },
  inputSelect: {
    width: "11vw",
    textAlign: "left",
  },
  inputSelectSection: {
    marginRight: "1rem",
  },
  inputSelectMobile: {
    width: "47vw",
    left: "5px",
  },
  inputSelectSearchMobile: {
    width: "47vw",
    right: "5px",
  },
  filterDialogInputMobile: {
    width: "100%",
    textAlign: "left",
  },
  buttonsSection: {
    width: "100%",
    alignContent: "flex-end",
  },
  wopTableSection: {
    width: "100%",
    marginTop: "1rem",
  },
  requestWOPButtonDesktop: {
    height: "40px",
    fontSize: "0.8rem",
    float: "right",
    marginTop: "7px",
    // marginLeft: "1vw",
    marginBottom: "-8px",
  },
  requestWOPButtonMobile: {
    width: "95.45vw",
  },
  buttonText: {
    color: theme.palette.secondary.swaWhite,
    "font-weight": "bold"
  },
}));

const EmployeeTimeOffWOPPage = (props) => {
  //#region Constants
  const classes = useStyles();
  const userSession = useSelector(selectUserSession);
  const stationFilter = useSelector(selectStationFilter);
  const employeeTimeOffRequestWOPData = useSelector(selectEmployeeTimeOffRequestWOPData);
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [isSearch, setIsSearch] = useState(false);
  const [isError, setIsError] = useState(false);
  const [errorObject, setErrorObject] = useState({});
  const [isUserAuthorizedState, setIsUserAuthorizedState] = useState(false);
  const [isUserAuthorizedStateLoading, setIsUserAuthorizedStateLoading] = useState(true);
  const [timeOffRequests, setTimeOffRequests] = useState([]);
  const [searchedTimeOffRequests, setSearchedTimeOffRequests] = useState([]);
  const [filteredTimeOffRequests, setFilteredTimeOffRequests] = useState([]);
  const [station, setStation] = useState("");
  const [isWOPListFrozen, setIsWOPListFrozen] = useState(false);
  const [wopFreezeData, setWopFreezeData] = useState(null);
  const [statusFilters, setStatusFilters] = useState(ALL_TOR_WOP_STATUSES);
  const breadcrumbsList = [BREADCRUMBS_TITLE.HOME, BREADCRUMBS_TITLE.EMPLOYEE_TIME_OFF_WOP];
  const breadcrumbsLinks = [HOME_PATH, props.featureConfig.path];
  const torTableDesktopRef = useGridApiRef();
  const webSocketEventData = useSelector(selectWebSocketEventData);
  const wopWebsocketDataRef = useRef({});
  const freezeWebsocketDataRef = useRef({});
  const isAdminPage = false;

  const [isMobileFilterDialogOpen, setIsMobileFilterDialogOpen] = useState(false);
  //#endregion

  //#region Hooks
  useEffect(() => {
    if (!isUserAuthorizedState) {
      isUserAuthorized();
    } else {
      const workgroup = getWorkgroupGivenJobCode(userSession.jobCode);
      const station = getEmployeeStation(props.featureConfig.userStations, userSession, stationFilter);
      dispatch(updateSelectedWorkgroup(workgroup));
      setStation(station);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserAuthorizedState]);

  useEffect(() => {
    if (employeeTimeOffRequestWOPData.selectedWorkgroup !== "") {
      const availableWOPTypes =
        TIME_OFF_WOP_TYPES_BY_CONTRACT[getUnionContractGivenWorkgroup(employeeTimeOffRequestWOPData.selectedWorkgroup)];
      dispatch(updateAvailableWopTypes(availableWOPTypes));
      if (availableWOPTypes.includes(employeeTimeOffRequestWOPData.selectedWopType)) {
        dispatch(updateSelectedWopType(employeeTimeOffRequestWOPData.selectedWopType));
      } else {
        dispatch(updateSelectedWopType(""));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeTimeOffRequestWOPData.selectedWorkgroup]);

  useEffect(() => {
    setStatusFilters(ALL_TOR_WOP_STATUSES);
    updateTimeOffRequestList();
    getLatestWOPFreezeEvent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    employeeTimeOffRequestWOPData.selectedWorkgroup,
    station,
    employeeTimeOffRequestWOPData.selectedWopType,
    employeeTimeOffRequestWOPData.selectedDate,
  ]);

  useEffect(() => {
    wopWebsocketDataRef.current = webSocketEventData.torAdminEvents;
    if (Object.values(webSocketEventData.torAdminEvents).length > 0) {
      const selectedDate = getSelectedDateSafely();
      if (
        selectedDate &&
        station &&
        employeeTimeOffRequestWOPData.selectedWopType &&
        employeeTimeOffRequestWOPData.selectedWorkgroup
      ) {
        const torWebsocketIncomingEvents = mergeWebSocketAdminTORs(webSocketEventData.torAdminEvents, []);
        let torWebsocketEventsToProcess = torWebsocketIncomingEvents.filter(
          (torEvent) =>
            torEvent.station === station &&
            torEvent.timeOffRequestType === employeeTimeOffRequestWOPData.selectedWopType &&
            torEvent.workgroup === employeeTimeOffRequestWOPData.selectedWorkgroup &&
            (torEvent.shiftStart.toISODate() === selectedDate.toISODate() ||
              torEvent.shiftEnd.toISODate() === selectedDate.toISODate())
        );

        if (isWOPListFrozen) {
          torWebsocketEventsToProcess = torWebsocketEventsToProcess.filter((torEvent) =>
            timeOffRequests.some((timeOffRequest) => timeOffRequest.torId === torEvent.torId)
          );
        }

        torWebsocketEventsToProcess.forEach((torRequest) => {
          longPollTORSnapshotByTorId(torRequest.torId, torRequest.status);
        });
      }
      wopWebsocketDataRef.current = {};
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webSocketEventData.torAdminEvents]);

  useEffect(() => {
    freezeWebsocketDataRef.current = webSocketEventData.freezeEvents;
    if (Object.values(webSocketEventData.freezeEvents).length > 0) {
      const selectedDate = getSelectedDateSafely();
      if (
        selectedDate &&
        station &&
        employeeTimeOffRequestWOPData.selectedWopType &&
        employeeTimeOffRequestWOPData.selectedWorkgroup
      ) {
        setStatusFilters(ALL_TOR_WOP_STATUSES);
        const freezeWebsocketIncomingEvents = mergeWebSocketFreezes(
          webSocketEventData.freezeEvents,
          station,
          employeeTimeOffRequestWOPData.selectedWopType,
          selectedDate.toISODate(),
          employeeTimeOffRequestWOPData.selectedWorkgroup,
          []
        );

        freezeWebsocketIncomingEvents.forEach(() => {
          getLatestWOPFreezeEvent();
          updateTimeOffRequestList();
        });
      }
      freezeWebsocketDataRef.current = {};
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webSocketEventData.freezeEvents]);
  //#endregion

  //#region API Calls
  const updateTimeOffRequestList = () => {
    const selectedDate = getSelectedDateSafely();
    if (
      station &&
      employeeTimeOffRequestWOPData.selectedWorkgroup &&
      selectedDate &&
      employeeTimeOffRequestWOPData.selectedWopType
    ) {
      setIsLoading(true);
      setIsError(false);
      setErrorObject({});
      fetchTimeOffRequestWOPEvents(
        station,
        employeeTimeOffRequestWOPData.selectedWorkgroup,
        selectedDate.toISO(),
        employeeTimeOffRequestWOPData.selectedWopType
      )
        .then((res) => res.json())
        .then((result) => {
          if (result.error) {
            setIsError(true);
            setErrorObject(timeOffRequestDataErrorCodeToErrorMessageMap.unexpectedTimeOffRequestDataError);
          } else {
            let scrubbedTimeOffRequests = getScrubbedTimeOffRequestSnapshots(result);
            setTimeOffRequests(scrubbedTimeOffRequests);

            if (!isStatusSelectAllChecked()) {
              const filteredRequests = scrubbedTimeOffRequests.filter((tor) =>
                statusFilters.includes(tor.visibleStatus)
              );
              setFilteredTimeOffRequests(filteredRequests);
            }
          }
        })
        .catch((err) => {
          console.error("error happened!", err);
          setIsError(true);
          setErrorObject(timeOffRequestDataErrorCodeToErrorMessageMap.unexpectedTimeOffRequestDataError);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  const getLatestWOPFreezeEvent = () => {
    const selectedDate = getSelectedDateSafely();
    setIsWOPListFrozen(false);
    setWopFreezeData(null);
    if (
      station &&
      employeeTimeOffRequestWOPData.selectedWorkgroup &&
      selectedDate &&
      employeeTimeOffRequestWOPData.selectedWopType
    ) {
      fetchLatestTimeOffRequestWOPFreezeEvent(
        station,
        employeeTimeOffRequestWOPData.selectedWorkgroup,
        getIsoDateFormat(selectedDate),
        employeeTimeOffRequestWOPData.selectedWopType
      )
        .then((res) => res.json())
        .then((result) => {
          if (result.error) {
            console.error("Error fetching latest WOP freeze event:", result.error);
          } else if (result?.latestWopFreezeEvent) {
            setWopFreezeData(result.latestWopFreezeEvent);
            setIsWOPListFrozen(result.latestWopFreezeEvent.isFrozen);
          }
        })
        .catch((error) => {
          console.error("Error fetching latest WOP freeze event:", error);
        });
    }
  };

  const longPollTORSnapshotByTorId = (torId, torStatus) => {
    //NOSONAR
    const startTime = DateTime.now();
    function fetchTORSnapshot() {
      fetchTimeOffRequestSnapshotByTorId(torId, true)
        .then((response) => response.json())
        .then((result) => {
          if (result?.torSnapshotObject?.torId === torId && result?.torSnapshotObject?.status === torStatus) {
            updateTimeOffRequestList();
          } else if (!result || result.error) {
            console.error("Error longPollTORSnapshotByTorId event: ", result);
          } else {
            const currentTime = DateTime.now();
            const elapsedTime = currentTime - startTime;
            if (elapsedTime < API_TIMEOUT) {
              // recursive call till requestId matches or timeout happens
              setTimeout(fetchTORSnapshot, API_POLLING_INTERVAL);
            }
          }
        })
        .catch((error) => {
          console.error("Error longPollTORSnapshotByTorId event:", error);
        });
    }
    fetchTORSnapshot();
  };
  //#endregion

  //#region Input Validations
  const isUserAuthorized = () => {
    isUserAuthorizedByServer(userSession).then((is200) => {
      if (is200) {
        setIsUserAuthorizedState(true);
      } else {
        logout();
      }
      setIsUserAuthorizedStateLoading(false);
    });
  };

  const applySearch = (searchString, searchedTimeOffRequests) => {
    if (searchString === "") {
      setIsSearch(false);
      setSearchedTimeOffRequests(searchedTimeOffRequests);
      applyTimeOffTableScroll();
      setIsLoading(false);
    } else {
      setIsSearch(true);
      setSearchedTimeOffRequests(searchedTimeOffRequests);
      applyTimeOffTableScroll();
      setIsLoading(false);
    }
  };

  const applyTimeOffTableScroll = () => {
    const currentDesktop = torTableDesktopRef.current;
    if (currentDesktop !== null && currentDesktop.getRowModels && currentDesktop.getRowModels().size > 0) {
      currentDesktop.scrollToIndexes({ rowIndex: 0, colIndex: 0 });
    }
  };

  const getSelectedDateSafely = () => {
    // This safe function is used to be sure the default value is provided in the event the redux state is not available yet
    return employeeTimeOffRequestWOPData.selectedDate
      ? deserializeDateFromRedux(employeeTimeOffRequestWOPData.selectedDate)
      : null;
  };
  //#endregion

  //#region Event Handlers
  const handleStationFilterChange = (event) => {
    //NOSONAR
    setStation(event.target.value);
    dispatch(updateStationFilter(event.target.value));
  };

  const handleWOPTypeInputChange = async (event) => {
    //NOSONAR
    dispatch(updateSelectedWopType(event.target.value));
    setTimeOffRequests([]);
    setSearchedTimeOffRequests([]);

    if (isLWOPTOWOPDayOfRequest(event.target.value)) {
      dispatch(updateSelectedDate(serializeDateToRedux(TODAY(station))));
    } else {
      dispatch(updateSelectedDate(serializeDateToRedux(TOMORROW(station))));
    }
  };

  const handleDateChange = (date) => {
    //NOSONAR
    dispatch(updateSelectedDate(serializeDateToRedux(date)));
    setTimeOffRequests([]);
    setSearchedTimeOffRequests([]);
  };

  const handleStatusFilterChange = (event) => {
    //NOSONAR
    let newStatusFilterSelectedList = event.target.value;
    setStatusFilters(newStatusFilterSelectedList);
    setIsLoading(true);
    const torRequestsToFilter = isSearch ? searchedTimeOffRequests : timeOffRequests;
    const filteredTors = torRequestsToFilter.filter((tor) => newStatusFilterSelectedList.includes(tor.visibleStatus));

    if (isSearch) {
      initializeWOPSearchIndex(filteredTors, isAdminPage);
      searchTimeOffRequestsWOP(employeeTimeOffRequestWOPData.searchString, applySearch);
    }

    setFilteredTimeOffRequests(filteredTors);
    setIsLoading(false);
    applyTimeOffTableScroll();
  };

  const isStatusSelectAllChecked = () => {
    return statusFilters.length === ALL_TOR_WOP_STATUSES.length;
  };

  const handleSearchChange = (event) => {
    //NOSONAR
    dispatch(updateSearchString(event.target.value));
  };

  const handleSearchBlur = (event) => {
    //NOSONAR
    if (event.target.value.trim() === "") {
      handleSearchSubmit("");
    }
  };

  const handleSearchEnterKeyPressed = (event) => {
    //NOSONAR
    if (event.key === "Enter") {
      handleSearchSubmit(employeeTimeOffRequestWOPData.searchString);
    }
  };

  const handleSearchCancel = () => {
    //NOSONAR
    dispatch(updateSearchString(""));
    handleSearchSubmit("");
  };

  const handleSearchSubmit = (newSearchString) => {
    //NOSONAR
    const selectedDate = getSelectedDateSafely();
    if (
      station &&
      employeeTimeOffRequestWOPData.selectedWorkgroup &&
      selectedDate &&
      employeeTimeOffRequestWOPData.selectedWopType
    ) {
      dispatch(updateSearchStringErrors(""));
      const newSearchStringTrimmed = newSearchString.trim();
      if (
        !isNaN(parseInt(newSearchStringTrimmed)) &&
        parseInt(newSearchStringTrimmed) >= 10000000 &&
        parseInt(newSearchStringTrimmed) <= 99999999
      ) {
        dispatch(updateEmployeeTimeOffRequestFoundByReferenceNumberSearchIsLoading(true));
        fetchTimeOffRequestSnapshotByReferenceNumber(newSearchStringTrimmed, userSession.eid, station, isAdminPage)
          .then((result) => {
            if (result.message) {
              console.error("Error fetching time off request snapshot by reference number:", result.message);
              dispatch(updateSearchStringErrors(result.message));
              dispatch(updateEmployeeTimeOffRequestFoundByReferenceNumberSearchIsLoading(false));
            } else {
              let timeOffRequestSnapshotResult = result.torSnapshotObject;
              let scrubbedTimeOffRequestSnapshot = getScrubbedTimeOffRequestSnapshot(timeOffRequestSnapshotResult);
              if (scrubbedTimeOffRequestSnapshot.timeOffRequestType === employeeTimeOffRequestWOPData.selectedWopType) {
                dispatch(updateEmployeeTimeOffRequestFoundByReferenceNumberSearch(timeOffRequestSnapshotResult));
                dispatch(updateEmployeeTimeOffRequestFoundByReferenceNumberSearchIsLoading(false));
                applySearch(newSearchStringTrimmed, [scrubbedTimeOffRequestSnapshot]);
                dispatch(updateEmployeeTimeOffRequestFoundByReferenceNumberSearchIsDialogOpen(true));
              } else {
                dispatch(updateEmployeeTimeOffRequestFoundByReferenceNumberSearchIsLoading(false));
                dispatch(updateEmployeeTimeOffRequestFoundByReferenceNumberSearchIsDialogOpen(true));
              }
            }
          })
          .catch((err) => {
            console.error("error happened!", err);
            dispatch(updateEmployeeTimeOffRequestFoundByReferenceNumberSearchIsLoading(false));
            dispatch(updateEmployeeTimeOffRequestFoundByReferenceNumberSearchIsDialogOpen(false));
          });
      } else {
        const torsToSearch = isStatusSelectAllChecked() ? timeOffRequests : filteredTimeOffRequests;
        initializeWOPSearchIndex(torsToSearch, isAdminPage);
        searchTimeOffRequestsWOP(newSearchStringTrimmed, applySearch);
      }
    }
  };

  const handleClickMobileFilterDialogOpen = () => {
    //NOSONAR
    setIsMobileFilterDialogOpen(true);
  };

  const handleClickMobileFilterDialogClose = () => {
    //NOSONAR
    setIsMobileFilterDialogOpen(false);
  };

  const handleClickMobileFilterDialogApplyFiltersButton = () => {
    //NOSONAR
    setIsMobileFilterDialogOpen(false);
  };

  //#endregion

  //#region Component Renders
  const renderStationFilterSelect = () => {
    return (
      <StarsSelectInput
        label={STATION}
        menuMessage={STATION}
        value={stationFilter}
        allOptions={getEmployeeStationList(userSession)}
        sortComparator={sortOnCaseSensitiveTexts}
        onChange={(event) => handleStationFilterChange(event)}
        errors={stationFilter === "" ? STATION_FILTER_MUST_BE_SELECTED : ""}
        className={props.isDesktopView ? classes.inputStation : classes.filterDialogInputMobile}
        isMenuItemDisabled={(station) => !props?.featureConfig?.userStations?.includes(station)}
        disabled={getEmployeeStationList(userSession).length <= 1}
        fullWidth={isMobileFilterDialogOpen}
      />
    );
  };

  const renderWorkgroupSelect = () => {
    return (
      <TextField
        label="Workgroup"
        variant="outlined"
        value={employeeTimeOffRequestWOPData.selectedWorkgroup}
        disabled
        className={props.isDesktopView ? classes.inputSelect : classes.filterDialogInputMobile}
      />
    );
  };

  const renderWopTypeSelect = () => {
    return (
      <StarsSelectInput
        disabled={employeeTimeOffRequestWOPData.selectedWorkgroup === ""}
        label="Type"
        menuMessage="Type"
        value={employeeTimeOffRequestWOPData.selectedWopType}
        allOptions={employeeTimeOffRequestWOPData.availableWopTypes}
        errors=""
        sortComparator={sortOnCaseSensitiveTexts}
        onChange={(event) => handleWOPTypeInputChange(event)}
        fullWidth={isMobileFilterDialogOpen}
        className={
          props.isDesktopView
            ? classes.inputSelect
            : isMobileFilterDialogOpen
            ? classes.filterDialogInputMobile
            : classes.inputSelectMobile
        }
      />
    );
  };

  const renderDateSelect = () => {
    return (
      <StarsDatePicker
        label="Date"
        date={getSelectedDateSafely()}
        onChange={handleDateChange}
        dateErrors=""
        className={props.isDesktopView ? classes.inputSelect : classes.filterDialogInputMobile}
        minDate={convert_Luxon_To_DateFns(TODAY(station))}
        maxDate={convert_Luxon_To_DateFns(FIFTEEN_DAYS_IN_FUTURE(station))}
      />
    );
  };

  const renderStatusSelect = () => {
    return (
      <StarsSelectInput
        label="Status"
        menuMessage="Filter on Status"
        value={statusFilters}
        allOptions={ALL_TOR_WOP_STATUSES}
        sortComparator={sortOnCaseSensitiveTexts}
        errors={statusFilters.length <= 0 ? STATUS_FILTER_MUST_BE_SELECTED : ""}
        className={props.isDesktopView ? classes.inputSelect : classes.filterDialogInputMobile}
        onChange={handleStatusFilterChange.bind(this)}
        multiple={true}
        fullWidth={isMobileFilterDialogOpen}
      />
    );
  };

  const renderSearchInput = () => {
    return (
      <TextField
        label="Search"
        variant="outlined"
        value={employeeTimeOffRequestWOPData.searchString}
        onChange={handleSearchChange.bind(this)}
        onBlur={handleSearchBlur.bind(this)}
        onKeyUp={handleSearchEnterKeyPressed.bind(this)}
        className={props.isDesktopView ? classes.inputSelect : classes.inputSelectSearchMobile}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              {employeeTimeOffRequestWOPData.searchString !== "" ? (
                <CancelIconButton onSearchCancel={handleSearchCancel} />
              ) : (
                ""
              )}
              <SearchButton
                onSearchSubmit={handleSearchSubmit}
                searchString={employeeTimeOffRequestWOPData.searchString}
              />
            </InputAdornment>
          ),
        }}
        error={employeeTimeOffRequestWOPData.searchStringErrors !== ""}
        helperText={employeeTimeOffRequestWOPData.searchStringErrors}
      />
    );
  };

  const renderRequestWOPButton = () => {
      const tradeFormChildItems = props.featureConfig.childItems;
      const requestTimeOff = tradeFormChildItems.find((item) => item.key === FEATURES.REQUEST_TIME_OFF);
      if (!!requestTimeOff) {
        return (
          <Button
            className={props.isDesktopView ? classes.requestWOPButtonDesktop : classes.requestWOPButtonMobile}
            href={`#${requestTimeOff.path}`}
            color="primary"
            variant="contained"
          >
            <Typography variant="body2" className={classes.buttonText}>
              {REQUEST_WOP_TITLE.toUpperCase()}
            </Typography>
          </Button>
        );
      } else {
        return "";
      }
    };

  const renderDesktopEmployeeTimeOffWOPPage = () => {
    return (
      <div className={classes.root}>
        <Grid>
          <StarsBreadcrumbs
            isConfirmationRequired={false}
            breadcrumbsLinks={breadcrumbsLinks}
            breadcrumbsList={breadcrumbsList}
          />
        </Grid>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item container justifyContent="flex-start" md={8} lg={8}>
            <Grid container className={classes.pageTitle}>
              LWOP/TOWOP List
            </Grid>
            <br />
          </Grid>
        </Grid>

        <Grid
          container
          sx={{
            pt: 3,
            display: "flex",
            justifyContent: "start",
            paddingTop: "10px",
            paddingLeft: "3rem",
            paddingRight: "3rem",
            paddingBottom: "10px",
          }}
          spacing={2}
        >

          <Grid container alignContent="space-between">
            <Grid item xs={9} >
              <Grid container spacing={1} >
                <Grid item xs={.9}>{renderStationFilterSelect()}</Grid>
                <Grid item xs={1.9}>{renderWorkgroupSelect()}</Grid>
                <Grid item xs={1.9}>{renderWopTypeSelect()}</Grid>
                <Grid item xs={1.9}>{renderDateSelect()}</Grid>
                <Grid item xs={1.9}>{renderStatusSelect()}</Grid>
                <Grid item xs={1.9}>{renderSearchInput()}</Grid>
                
              </Grid>
            </Grid>
            <Grid item xs={3}>
              {renderRequestWOPButton()}
            </Grid>
          </Grid>

          <div className={classes.wopTableSection}>
            <TimeOffRequestWOPTable
              timeOffRequests={
                !isWOPListFrozen
                  ? isSearch
                    ? searchedTimeOffRequests
                    : isStatusSelectAllChecked()
                    ? timeOffRequests
                    : filteredTimeOffRequests
                  : []
              }
              wopType={employeeTimeOffRequestWOPData.selectedWopType}
              selectedDate={getSelectedDateSafely()}
              isLoading={isLoading}
              isError={isError}
              errorObject={errorObject}
              station={station}
              torTableDesktopRef={torTableDesktopRef}
              isWOPListFrozen={isWOPListFrozen}
              wopFreezeData={wopFreezeData}
              isAdminPage={isAdminPage}
              isDesktopView={props.isDesktopView}
            />
          </div>
        </Grid>
      </div>
    );
  };

  const renderMobileEmployeeTimeOffWOPPage = () => {
    return (
      <div data-testid="employee-time-off-wop-mobile-page">
        <EmployeeTimeOffWOPMobilePage
          timeOffRequests={
            !isWOPListFrozen
              ? isSearch
                ? searchedTimeOffRequests
                : isStatusSelectAllChecked()
                ? timeOffRequests
                : filteredTimeOffRequests
              : []
          }
          searchInput={renderSearchInput()}
          wopTypeSelect={renderWopTypeSelect()}
          isWOPListFrozen={isWOPListFrozen}
          handleClickMobileFilterDialogOpen={handleClickMobileFilterDialogOpen}
          renderRequestWOPButton={renderRequestWOPButton}
          isLoading={isLoading}
          isDesktopView={props.isDesktopView}
        />
        <EmployeeTimeOffWOPMobilePageFilterDialog
          stationSelect={renderStationFilterSelect()}
          workgroupSelect={renderWorkgroupSelect()}
          wopTypeSelect={renderWopTypeSelect()}
          dateSelect={renderDateSelect()}
          statusSelect={renderStatusSelect()}
          selectedWopType={employeeTimeOffRequestWOPData.selectedWopType}
          selectedDate={getSelectedDateSafely()}
          isMobileFilterDialogOpen={isMobileFilterDialogOpen}
          handleClickMobileFilterDialogClose={handleClickMobileFilterDialogClose}
          handleClickMobileFilterDialogApplyFiltersButton={handleClickMobileFilterDialogApplyFiltersButton}
        />
      </div>
    );
  };

  if (!isUserAuthorizedState && !isUserAuthorizedStateLoading) {
    return <UnauthorizedMessage />;
  }

  return (
    <div data-testid="employee-time-off-wop-page">
      {isUserAuthorizedStateLoading ? (
        <PageLoading />
      ) : (
        <>{props.isDesktopView ? renderDesktopEmployeeTimeOffWOPPage() : renderMobileEmployeeTimeOffWOPPage()}</>
      )}
    </div>
  );
  //#endregion
};

EmployeeTimeOffWOPPage.propTypes = {
  featureConfig: PropTypes.object.isRequired,
  isDesktopView: PropTypes.bool.isRequired,
};

export default EmployeeTimeOffWOPPage;
